import {MessageDescriptor} from 'react-intl';

import {
    choiceResponseTemplate,
    multichoiceResponseTemplate,
    newChoiceResponseTemplate,
    newMultichoiceResponseTemplate,
    newStringResponseTemplate,
    stringResponseTemplate,
} from 'assessment-editor/templates';
import {IconSymbol} from 'elements/components/Icons';
// Many models are defined by the backend and we get them via the
// auto-generated API client:
import {
    ItemMetadataTypeEnum,
    LibraryGeneralPermissions,
    ResourceRelationshipTargetItemTypeEnum
} from 'labxchange-client';
import {hasKey} from 'utils';
import messages from './displayMessages';

// Type alias for convenience. Use this instead. Other types from
// labxchange-client should be used directly for consistency.
import ItemType = ItemMetadataTypeEnum;

export { ItemType };

export interface ItemTypeMeta {
    type: ItemType;
    createPermissionName: string;
    editor: boolean | undefined;
    icon: IconSymbol;
    imageFile: string | null;
    defaultCardImage: string | null;
    name: MessageDescriptor;
    // about title is a heading text like "About this image".
    // We can't just combine "About this" + "image" because of languages with gendered articles etc.
    aboutTitle: MessageDescriptor;
    // item templates which are item type meta too. For example: Text -> Blank Page / Review / etc
    subTypes?: ItemSubTypeMeta[];
    hasDiscussion?: boolean;
    simpleName?: MessageDescriptor;
}

export interface ItemSubTypeMeta {
    type: ItemType;
    slug: string;
    icon: IconSymbol;
    imageFile: string | null;
    defaultCardImage: string | null;
    name: MessageDescriptor;
    aboutTitle: MessageDescriptor;
    // defaultValues should map blockEditorState interface to be properly filled
    defaultValues: {};
}

const itemTypeMetas: {[T in ItemType]: ItemTypeMeta} = {
    annotated_video: {
        type: ItemType.AnnotatedVideo,
        createPermissionName: 'canCreateAnnotatedVideoItem',
        editor: true,
        icon: 'device-camera-video',
        imageFile: 'Annotated_Video.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Annotated_Video.svg',
        name: messages.itemTypeAnnotatedVideoBeta,
        aboutTitle: messages.itemTypeAboutAnnotatedVideo,
    },
    assignment: {
        type: ItemType.Assignment,
        createPermissionName: 'canCreateAssignmentItem',
        editor: true,
        icon: 'assessment',
        imageFile: 'Assignment.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Assignment.svg',
        name: messages.itemTypeAssignment,
        aboutTitle: messages.itemTypeAboutAssignment,
    },
    assessment: {
        type: ItemType.Assessment,
        createPermissionName: 'canCreateAssessmentItem',
        editor: true,
        icon: 'assessment',
        imageFile: 'Assessment.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Assessment.svg',
        name: messages.itemTypeAssessment,
        aboutTitle: messages.itemTypeAboutAssessment,
        subTypes: [
            {
                type: ItemType.Assessment,
                slug: 'multi-select',
                icon: 'assessment',
                imageFile: 'Multi_select.svg',
                defaultCardImage: '/assets/images/library-items-defaults/Multi_select.svg',
                name: messages.itemTypeAssessmentTemplateMultiSelect,
                aboutTitle: messages.itemTypeAboutAssessment,
                defaultValues: {
                    olx: choiceResponseTemplate,
                },
            },
            {
                type: ItemType.Assessment,
                slug: 'short-answer',
                icon: 'assessment',
                imageFile: 'Short_answer.svg',
                defaultCardImage: '/assets/images/library-items-defaults/Short_answer.svg',
                name: messages.itemTypeAssessmentTemplateShortAnswer,
                aboutTitle: messages.itemTypeAboutAssessment,
                defaultValues: {
                    olx: stringResponseTemplate,
                },
            },
            {
                type: ItemType.Assessment,
                slug: 'single-select',
                icon: 'assessment',
                imageFile: 'Single_select.svg',
                defaultCardImage: '/assets/images/library-items-defaults/Single_Select.svg',
                name: messages.itemTypeAssessmentTemplateSingleSelect,
                aboutTitle: messages.itemTypeAboutAssessment,
                defaultValues: {
                    olx: multichoiceResponseTemplate,
                },
            },
        ],
    },
    audio: {
        type: ItemType.Audio,
        createPermissionName: 'canCreateAudioItem',
        editor: true,
        icon: 'audio',
        imageFile: 'Audio.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Audio.svg',
        name: messages.itemTypeAudio,
        aboutTitle: messages.itemTypeAboutAudio,
        hasDiscussion: true,
    },
    book: {
        type: ItemType.Book,
        createPermissionName: 'canCreateBookItem',
        editor: false,
        icon: 'book',
        imageFile: null,
        defaultCardImage: null,
        name: messages.itemTypeBook,
        aboutTitle: messages.itemTypeAboutBook,
    },
    case_study: {
        type: ItemType.CaseStudy,
        createPermissionName: 'canCreateCaseStudyItem',
        editor: true,
        icon: 'case-study',
        imageFile: 'Case_study.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Case_study.svg',
        name: messages.itemTypeCaseStudy,
        aboutTitle: messages.itemTypeAboutCaseStudy,
        hasDiscussion: true,
    },
    cluster: {
        type: ItemType.Cluster,
        createPermissionName: 'canCreateClusterItem',
        editor: undefined,
        icon: 'cluster',
        imageFile: null,
        defaultCardImage: null,
        name: messages.simpleItemTypeCluster,
        simpleName: messages.simpleItemTypeCluster,
        aboutTitle: messages.itemTypeAboutCluster,
    },
    document: {
        type: ItemType.Document,
        createPermissionName: 'canCreateDocumentItem',
        editor: true,
        icon: 'document',
        imageFile: 'Document.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Document.svg',
        name: messages.itemTypeDocument,
        aboutTitle: messages.itemTypeAboutDocument,
        hasDiscussion: true,
    },
    lx_text: {
        type: ItemType.LxText,
        createPermissionName: 'canCreateTextItem',
        editor: false,
        icon: 'text',
        imageFile: 'Text.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Text.svg',
        name: messages.itemTypeHtmlText,
        aboutTitle: messages.itemTypeAboutText,
        hasDiscussion: true,
    },
    image: {
        type: ItemType.Image,
        createPermissionName: 'canCreateImageItem',
        editor: true,
        icon: 'image',
        imageFile: 'Image.svg',
        defaultCardImage: null,
        name: messages.itemTypeImage,
        aboutTitle: messages.itemTypeAboutImage,
        hasDiscussion: true,
    },
    interactive: {
        type: ItemType.Interactive,
        createPermissionName: 'canCreateInteractiveItem',
        editor: true,
        icon: 'interactive',
        imageFile: 'Interactive.svg',
        defaultCardImage: null,
        name: messages.itemTypeInteractive,
        aboutTitle: messages.itemTypeAboutInteractive,
        hasDiscussion: true,
    },
    narrative: {
        type: ItemType.Narrative,
        createPermissionName: 'canCreateNarrativeItem',
        editor: true,
        icon: 'narrative',
        imageFile: 'Narrative.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Narratives.svg',
        name: messages.itemTypeNarrative,
        aboutTitle: messages.itemTypeAboutNarrative,
        hasDiscussion: true,
    },
    pathway: {
        type: ItemType.Pathway,
        createPermissionName: 'canCreatePathwayItem',
        editor: true,
        icon: 'pathway',
        imageFile: 'Pathway.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Pathway.svg',
        name: messages.itemTypePathway,
        simpleName: messages.simpleItemTypePathway,
        aboutTitle: messages.itemTypeAboutPathway,
        hasDiscussion: true,
    },
    simulation: {
        type: ItemType.Simulation,
        createPermissionName: 'canCreateSimulationItem',
        editor: true,
        icon: 'simulation',
        imageFile: 'Simulation.svg',
        defaultCardImage: null,
        name: messages.itemTypeSimulation,
        aboutTitle: messages.itemTypeAboutSimulation,
        hasDiscussion: true,
    },
    teaching_guide: {
        type: ItemType.TeachingGuide,
        createPermissionName: 'canCreateTeachingGuideItem',
        editor: true,
        icon: 'teaching-guide',
        imageFile: 'Teaching_guide.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Teaching_guide.svg',
        name: messages.itemTypeTeachingGuide,
        aboutTitle: messages.itemTypeAboutTeachingGuide,
        hasDiscussion: true,
    },
    text: {
        type: ItemType.Text,
        createPermissionName: 'canCreateTextItem',
        editor: true,
        icon: 'text',
        imageFile: 'Text.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Text.svg',
        name: messages.itemTypeText,
        aboutTitle: messages.itemTypeAboutText,
        hasDiscussion: true,
    },
    unknown: {
        type: ItemType.Unknown,
        createPermissionName: 'canCreateUnknownItem',
        editor: undefined,
        icon: 'settings',
        imageFile: null,
        defaultCardImage: null,
        name: messages.itemTypeUnknown,
        aboutTitle: messages.itemTypeAboutUnknown,
    },
    video: {
        type: ItemType.Video,
        createPermissionName: 'canCreateVideoItem',
        editor: true,
        imageFile: 'Video.svg',
        defaultCardImage: '/assets/images/library-items-defaults/video.svg',
        icon: 'device-camera-video',
        name: messages.itemTypeVideo,
        aboutTitle: messages.itemTypeAboutVideo,
        hasDiscussion: true,
    },
    lx_video: {
        type: ItemType.LxVideo,
        createPermissionName: 'canCreateLxVideoItem',
        editor: true,
        imageFile: 'Video.svg',
        defaultCardImage: '/assets/images/library-items-defaults/video.svg',
        icon: 'device-camera-video',
        name: messages.itemTypeLXVideo,
        aboutTitle: messages.itemTypeAboutVideo,
        hasDiscussion: true,
    },
    question: {
        type: ItemType.Question,
        createPermissionName: 'canCreateQuestionItem',
        editor: true,
        icon: 'assessment',
        imageFile: 'Assessment.svg',
        defaultCardImage: '/assets/images/library-items-defaults/Assessment.svg',
        name: messages.itemTypeQuestionNew,
        aboutTitle: messages.itemTypeAboutAssessment,
        subTypes: [
            {
                type: ItemType.Question,
                slug: 'multi-select',
                icon: 'assessment',
                imageFile: 'Multi_select.svg',
                defaultCardImage: '/assets/images/library-items-defaults/Multi_select.svg',
                name: messages.itemTypeAssessmentTemplateMultiSelect,
                aboutTitle: messages.itemTypeAboutAssessment,
                defaultValues: {
                    olx: newChoiceResponseTemplate,
                },
            },
            {
                type: ItemType.Question,
                slug: 'short-answer',
                icon: 'assessment',
                imageFile: 'Short_answer.svg',
                defaultCardImage: '/assets/images/library-items-defaults/Short_answer.svg',
                name: messages.itemTypeAssessmentTemplateShortAnswer,
                aboutTitle: messages.itemTypeAboutAssessment,
                defaultValues: {
                    olx: newStringResponseTemplate,
                },
            },
            {
                type: ItemType.Question,
                slug: 'single-select',
                icon: 'assessment',
                imageFile: 'Single_select.svg',
                defaultCardImage: '/assets/images/library-items-defaults/Single_Select.svg',
                name: messages.itemTypeAssessmentTemplateSingleSelect,
                aboutTitle: messages.itemTypeAboutAssessment,
                defaultValues: {
                    olx: newMultichoiceResponseTemplate,
                },
            },
        ],
    },
    link: {
        type: ItemType.Link,
        createPermissionName: 'canCreateLinkItem',
        editor: true,
        icon: 'google-doc',
        imageFile: 'GoogleDoc.svg',
        defaultCardImage: '/assets/images/library-items-defaults/GoogleDoc.svg',
        name: messages.itemTypeGoogleDoc,
        aboutTitle: messages.itemTypeAboutGoogleDoc,
    },
};

export function getItemTypeMeta(type: ItemType | ResourceRelationshipTargetItemTypeEnum): ItemTypeMeta {
    if (type in itemTypeMetas) {
        return itemTypeMetas[type];
    }
    return itemTypeMetas.unknown;
}

export function getItemTypeMetaSubTypes(type?: ItemType): ItemSubTypeMeta[] {
    if (type !== undefined && type in itemTypeMetas) {
        return itemTypeMetas[type].subTypes || [];
    }
    return [];
}

export function getItemSubTypeDefaultValues(type: ItemType, itemSubTypeSlug: string): {} {
    const itemSubTypes = getItemTypeMetaSubTypes(type);
    for (const itemSubType of itemSubTypes) {
        if (itemSubType.slug === itemSubTypeSlug) {
            return itemSubType.defaultValues;
        }
    }
    return {};
}

export function itemTypesUserCanCreate(permissions: LibraryGeneralPermissions): ItemType[] {
    const itemTypes: ItemType[] = [];
    for (const itemTypeMeta of Object.values(itemTypeMetas)) {
        // Check if there is an editor available and if the user has permission.
        if (itemTypeMeta.editor) {
            const permissionName = itemTypeMeta.createPermissionName;
            if (hasKey(permissions, permissionName) && permissions[permissionName]) {
                itemTypes.push(itemTypeMeta.type);
            }
        }
    }
    return itemTypes;
}
